import * as z from 'zod';

/**
 * Admin couriers domain (the migrated cpanel `/couriers/couriers-all` list),
 * served by the main gateway (`GET admin/couriers`).
 *
 * Wire tolerance rule (same as `entities/client`): every field is
 * optional/nullish and objects are `.loose()` — the backend evolves faster
 * than this screen, and a missing field must render its "—" fallback, never
 * crash the table.
 */

const numLike = z.union([z.string(), z.number()]);

/** Status segments (cpanel `courier_statuses`); `ALL` carries no wire filter. */
export const courierStatusFilterSchema = z.enum([
  'ALL',
  'ACTIVE',
  'INACTIVE',
  'NEED_VISIT',
  'BLOCKED',
  'UNACTIVATED',
  'FIRED',
]);
export type CourierStatusFilter = z.infer<typeof courierStatusFilterSchema>;

/** Activity quick-filter (cpanel `activiteOptions`). */
export const courierActivitySchema = z.enum([
  'has_activity',
  'has_scooter',
  'no_activity',
  'no_scooter',
]);
export type CourierActivity = z.infer<typeof courierActivitySchema>;

export const courierCashSchema = z
  .object({
    cash_balance: numLike.nullish(),
    updated_at: z.string().nullish(),
  })
  .loose();
export type CourierCash = z.infer<typeof courierCashSchema>;

/** One list row — the union of every field the table and edit dialog read. */
export const adminCourierRowSchema = z
  .object({
    id: numLike.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
    second_phone: z.string().nullish(),
    profile_photo: z.string().nullish(),
    status: z.string().nullish(),
    created_at: z.string().nullish(),
    equipment_id: numLike.nullish(),
    equipment_name: z.string().nullish(),
    balance: numLike.nullish(),
    scooter_balance: numLike.nullish(),
    limit_balance: numLike.nullish(),
    courier_cash: courierCashSchema.nullish(),
    avg_rating: numLike.nullish(),
    order_count: numLike.nullish(),
    total_order_price: numLike.nullish(),
    avg_order_price: numLike.nullish(),
    is_ibox_blocked: z.boolean().nullish(),
    is_order_blocked: z.boolean().nullish(),
    chat_id: numLike.nullish(),
    deal_id: numLike.nullish(),
    deal_date: z.string().nullish(),
    deal_accepted: z.boolean().nullish(),
    employment_status_set: z.boolean().nullish(),
    commissioner_is_set: z.boolean().nullish(),
    visited_office: z.boolean().nullish(),
    passed_test: z.boolean().nullish(),
    driving_license_approved: z.boolean().nullish(),
  })
  .loose();
export type AdminCourierRow = z.infer<typeof adminCourierRowSchema>;

/** Per-status badge counts (`active_count`, `total_count`, …). */
export const adminCourierStatsSchema = z.record(z.string(), numLike);
export type AdminCourierStats = z.infer<typeof adminCourierStatsSchema>;

/** List envelope: `last_page` is the PAGE count (MUI `count` in the old grid). */
export const adminCourierListSchema = z.object({
  data: z.array(adminCourierRowSchema).default([]),
  last_page: z.number().nullish(),
  stats: adminCourierStatsSchema.nullish(),
});
export type AdminCourierList = z.infer<typeof adminCourierListSchema>;

/** Equipment option for the transport filter (`GET admin/equipments`). */
export const equipmentOptionSchema = z
  .object({
    id: numLike.nullish(),
    name_ru: z.string().nullish(),
  })
  .loose();
export type EquipmentOption = z.infer<typeof equipmentOptionSchema>;

/** Bonus template (`GET admin/bonuses`). */
export const bonusOptionSchema = z
  .object({
    id: numLike.nullish(),
    amount: numLike.nullish(),
  })
  .loose();
export type BonusOption = z.infer<typeof bonusOptionSchema>;

/** Foul rule template (`GET admin/fouls?is_rule=true`). */
export const foulOptionSchema = z
  .object({
    id: numLike.nullish(),
    reason_ru: z.string().nullish(),
    reason_uz: z.string().nullish(),
    reason_en: z.string().nullish(),
  })
  .loose();
export type FoulOption = z.infer<typeof foulOptionSchema>;

/** Courier order for the foul dialog's order select (`GET admin/orders`). */
export const courierOrderOptionSchema = z
  .object({
    id: numLike.nullish(),
    display_id: numLike.nullish(),
  })
  .loose();
export type CourierOrderOption = z.infer<typeof courierOrderOptionSchema>;

/** Latest block record (`GET admin/couriers/:id/block`) for the status hint. */
export const courierBlockRecordSchema = z
  .object({
    reason: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
  })
  .loose();
export type CourierBlockRecord = z.infer<typeof courierBlockRecordSchema>;
